import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled } from '@mui/material/styles';
import { DataService } from 'src/services/data-service';
import LeaseEditDialog from './LeaseEditDialog';

interface LeaseDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  leaseId: string;
  onLeaseUpdated?: () => void;
}

interface LeaseDetails {
  id: string;
  createdDate: string;
  startDate: string;
  endDate: string;
  status: string;
  refNumber: string;
  leaseType: string;
  leaseTypeId: string;
  currency: string;
  currencyId: string;
  unit: {
    id: string;
    number: string;
    rooms: number;
    bathrooms: number;
    floor: number;
    areaM2: number;
    propertyId: string;
  };
  tenant: {
    id: string;
    fullName: string;
    phoneNumbers: string[];
    emails: string[];
    nationality: string;
    identificationType: string;
  };
  fees: Array<{
    id: string;
    value: number;
    type: string;
    currency: string;
    billingCycle: string;
    notes: string | null;
    active: boolean;
  }>;
  active: boolean;
}

const LeaseStatus: Record<string, { label: string; color: 'warning' | 'success' | 'error' | 'default' | 'info' }> = {
  'Draft': { label: 'Draft', color: 'warning' },
  'Active': { label: 'Active', color: 'success' },
  'Expired': { label: 'Expired', color: 'error' },
  'Terminated': { label: 'Terminated', color: 'default' }
};

const SectionHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(0.75),
  marginBottom: theme.spacing(1.5)
}));

const LeaseDetailsDialog: React.FC<LeaseDetailsDialogProps> = ({
  open,
  onClose,
  leaseId,
  onLeaseUpdated
}) => {
  const [leaseDetails, setLeaseDetails] = useState<LeaseDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activating, setActivating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open && leaseId) {
      fetchLeaseDetails();
    }
  }, [open, leaseId]);

  const fetchLeaseDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DataService.get(`api/Lease/get/${leaseId}`);

      if (response.ok) {
        const data = await response.json();
        console.log('Lease Details:', data);
        setLeaseDetails(data.success ? data.data : data);
      } else {
        throw new Error('Failed to fetch lease details');
      }
    } catch (err) {
      console.error('Error fetching lease details:', err);
      setError('Failed to load lease details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return `${currency} ${amount.toLocaleString()}`;
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusInfo = (status: string) => {
    return LeaseStatus[status] || { label: status || 'Unknown', color: 'default' as const };
  };

  const handleActivateLease = async () => {
    setActivating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await DataService.get(`api/Lease/activate/${leaseId}`);

      if (response.ok) {
        setSuccessMessage('Lease activated successfully!');
        // Refresh the lease details
        await fetchLeaseDetails();
        // Notify parent to refresh list
        if (onLeaseUpdated) {
          onLeaseUpdated();
        }
      } else {
        let errorMessage = `Failed to activate lease (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.title || JSON.stringify(errorData);
        } catch (e) {
          errorMessage = `API Error: ${response.status} ${response.statusText}`;
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error activating lease:', err);
      setError('An error occurred while activating the lease');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !leaseDetails) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight="bold">Error</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error">{error || 'Lease details not found'}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const statusInfo = getStatusInfo(leaseDetails.status);
  const daysRemaining = getDaysRemaining(leaseDetails.endDate);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <DescriptionIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Lease Details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {leaseDetails.id.substring(0, 8)}...
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              label={statusInfo.label}
              color={statusInfo.color}
              sx={{ fontWeight: 'bold' }}
            />
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
        <Grid container spacing={3}>
          {/* Lease Information */}
          <Grid item xs={12}>
            <SectionHeader>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarTodayIcon />
                <Typography variant="h6" fontWeight="bold">
                  Lease Information
                </Typography>
              </Box>
            </SectionHeader>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Lease Type
                  </Typography>
                  <Typography variant="h6" fontWeight="600" textTransform="uppercase">
                    {leaseDetails.leaseType}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Currency
                  </Typography>
                  <Typography variant="h6" fontWeight="600">
                    {leaseDetails.currency}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Created Date
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatDate(leaseDetails.createdDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      label={statusInfo.label}
                      color={statusInfo.color}
                      size="small"
                      sx={{ fontWeight: 'bold', mt: 0.5 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatDate(leaseDetails.startDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatDate(leaseDetails.endDate)}
                  </Typography>
                </Grid>
                {daysRemaining > 0 && (
                  <Grid item xs={12}>
                    <Chip
                      label={`${daysRemaining} days remaining`}
                      color={daysRemaining < 30 ? 'warning' : 'info'}
                      variant="outlined"
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Tenant Information */}
          <Grid item xs={12} md={6}>
            <SectionHeader>
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon />
                <Typography variant="h6" fontWeight="bold">
                  Tenant Information
                </Typography>
              </Box>
            </SectionHeader>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <PersonIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    {leaseDetails.tenant.fullName}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <PublicIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {leaseDetails.tenant.nationality}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Email Addresses
                  </Typography>
                  {leaseDetails.tenant.emails.map((email, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1} mt={0.5}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{email}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Phone Numbers
                  </Typography>
                  {leaseDetails.tenant.phoneNumbers.map((phone, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1} mt={0.5}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2">{phone}</Typography>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Unit Information */}
          <Grid item xs={12} md={6}>
            <SectionHeader>
              <Box display="flex" alignItems="center" gap={1}>
                <HomeIcon />
                <Typography variant="h6" fontWeight="bold">
                  Unit Information
                </Typography>
              </Box>
            </SectionHeader>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight="bold" color="primary" mb={3}>
                Unit {leaseDetails.unit.number}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                    <MeetingRoomIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="600" mt={1}>
                      {leaseDetails.unit.rooms}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Rooms
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                    <BathtubIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="600" mt={1}>
                      {leaseDetails.unit.bathrooms}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Bathrooms
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                    <SquareFootIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="600" mt={1}>
                      {leaseDetails.unit.areaM2}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Area (m²)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                    <HomeIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="600" mt={1}>
                      {leaseDetails.unit.floor}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Floor
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Fees Information */}
          <Grid item xs={12}>
            <SectionHeader>
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoneyIcon />
                <Typography variant="h6" fontWeight="bold">
                  Fee Configuration ({leaseDetails.fees.length})
                </Typography>
              </Box>
            </SectionHeader>
            {leaseDetails.fees.length === 0 ? (
              <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
                <AttachMoneyIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No fees configured
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {leaseDetails.fees.map((fee) => (
                  <Grid item xs={12} sm={6} md={4} key={fee.id}>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2.5,
                        height: '100%',
                        borderLeft: 5,
                        borderColor: 'success.main',
                        bgcolor: 'grey.50'
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1.5}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          {fee.type}
                        </Typography>
                        {fee.active && (
                          <Chip label="Active" color="success" size="small" />
                        )}
                      </Box>
                      <Box mb={2}>
                        <Typography variant="h5" fontWeight="bold" color="success.main">
                          {formatCurrency(fee.value, fee.currency)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Per {fee.billingCycle}
                        </Typography>
                      </Box>
                      {fee.notes && (
                        <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1, mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Notes:
                          </Typography>
                          <Typography variant="body2">
                            {fee.notes}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {/* Show Activate button only for Draft leases */}
        {leaseDetails.status === 'Draft' && (
          <Button
            onClick={handleActivateLease}
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            disabled={activating}
          >
            {activating ? 'Activating...' : 'Activate Lease'}
          </Button>
        )}
        <Button
          onClick={() => setEditDialogOpen(true)}
          variant="contained"
          startIcon={<EditIcon />}
        >
          Edit Lease
        </Button>
      </DialogActions>

      {/* Edit Lease Dialog */}
      <LeaseEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        leaseId={leaseId}
        onSuccess={() => {
          setEditDialogOpen(false);
          fetchLeaseDetails(); // Refresh the details
          if (onLeaseUpdated) {
            onLeaseUpdated(); // Notify parent to refresh list
          }
        }}
      />
    </Dialog>
  );
};

export default LeaseDetailsDialog;
